/**
 * Sanitise a `from=` query-param value before redirecting after sign-in.
 *
 * Two shapes are accepted:
 *
 *   1. **Same-origin path** — starts with `/` but not `//`. The browser
 *      resolves it against the current host, so this is always safe.
 *   2. **Cross-host absolute URL on the same parent domain** — e.g. the
 *      OAuth shim at `https://coauthor.skarp.app/oauth/authorize?...`
 *      bouncing the user through the SPA login at
 *      `https://app.coauthor.skarp.app/login?from=<that URL>`. The
 *      browser would otherwise refuse to send the SPA's cookie on the
 *      cross-host redirect, but with the cookie scoped to the parent
 *      domain (`COAUTHOR_COOKIE_DOMAIN=coauthor.skarp.app`) this works.
 *
 * Everything else falls back to `/`. This blocks open-redirect attacks
 * (`?from=//evil.com` or `?from=https://evil.com/phishing`) — only
 * redirects that stay inside the Coauthor product are allowed.
 *
 * The trusted-host allowlist is intentionally hard-coded for simplicity;
 * if we ever add more sub-apps we update this list.
 */
const TRUSTED_HOSTS: ReadonlyArray<string> = [
  "coauthor.skarp.app",
  "app.coauthor.skarp.app",
  "app-dev.coauthor.skarp.app",
];

function _isTrustedHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return TRUSTED_HOSTS.includes(h) || h.endsWith(".coauthor.skarp.app");
}

export function safeRedirect(raw: string | null): string {
  if (!raw) return "/";
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return "/";
  }
  // 1. Same-origin path
  if (decoded.startsWith("/") && !decoded.startsWith("//")) {
    return decoded;
  }
  // 2. Cross-host absolute URL on a trusted parent-domain host
  try {
    const u = new URL(decoded);
    if (u.protocol !== "https:" && u.protocol !== "http:") return "/";
    if (!_isTrustedHost(u.hostname)) return "/";
    return decoded;
  } catch {
    return "/";
  }
}

/** True iff `target` is a cross-host URL (caller should `window.location.href = target`
 *  rather than using react-router's `navigate`, which only handles same-host paths). */
export function isCrossHost(target: string): boolean {
  return target.startsWith("http://") || target.startsWith("https://");
}
